import * as React from 'react'

import { classList } from 'react-classlist-helper'

import { CloseIcon } from '~/client/src/shared/components/icons/CloseIcon'
import { getDeletedObjectLabel } from '~/client/src/shared/utils/getDeletedObjectLabel'

// localization: translated

export interface IResourceChipItem {
  id: string
  /** Absent when the entity is gone and its last known name is unrecoverable. */
  label?: string
  isDeleted?: boolean
  // Equipment carries its own glyph/tint; renderIcon reads these to match the list views.
  color?: string | null
  iconName?: string | null
}

/** The chip's own label rule, for surfaces rendering the same item as text. */
export const getResourceChipLabel = (item: IResourceChipItem): string =>
  item.isDeleted ? getDeletedObjectLabel(item.label) : item.label

interface IProps {
  item: IResourceChipItem
  onRemove?: (id: string) => void
  onClick?: (id: string) => void
  isRemovable?: boolean
  icon?: React.ReactNode
}

const ResourceChip: React.FC<IProps> = ({
  item,
  onRemove,
  onClick,
  isRemovable = true,
  icon,
}) => {
  const handleRemove = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onRemove?.(item.id)
    },
    [onRemove, item.id],
  )

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onClick?.(item.id)
    },
    [onClick, item.id],
  )

  return (
    <div
      className={classList({
        'tw:inline-flex tw:items-center tw:min-w-0 tw:max-w-[300px] tw:h-5 tw:rounded tw:my-0.5 tw:mr-1': true,
        'tw:bg-muted': true,
        'tw:cursor-pointer': !!onClick,
      })}
      onClick={onClick ? handleClick : undefined}
    >
      {icon && (
        <span className="tw:inline-flex tw:items-center tw:shrink-0">
          {icon}
        </span>
      )}
      <span className="tw:text-sm tw:leading-5 tw:truncate tw:min-w-0 tw:px-1">
        {getResourceChipLabel(item)}
      </span>
      {isRemovable && (
        <button
          type="button"
          className="tw:inline-flex tw:items-center tw:shrink-0 tw:ml-0.5 tw:mr-0.5 tw:p-0 tw:border-0 tw:bg-transparent tw:cursor-pointer tw:text-muted-foreground"
          onClick={handleRemove}
        >
          <CloseIcon size="xs" />
        </button>
      )}
    </div>
  )
}

export default ResourceChip
